import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { match } from "ts-pattern";

import { Routes } from "src/utils/routes";
import { CallCloudCodeInput, CallCloudCodePayload, CloudCodeFunction, Report } from "src/generated/types";
import { parseError } from "src/utils/parse_error";
import { Modal } from "../modal/modal";
import { Body } from "../typography/typography";
import { Button } from "../button/button";

import { CTA } from "./cta";

/*
 * GraphQL
 */

const GENERATE_REPORT_MUTATION = gql`
    mutation GenerateReport($input: CallCloudCodeInput!) {
        callCloudCode(input: $input) {
            result
        }
    }
`;

/*
 * Types
 */

interface ApolloMutationVars {
    input: CallCloudCodeInput;
}

interface ApolloMutationData {
    updateReport: CallCloudCodePayload;
}

interface Props {
    currentReport: Report;
    schoolId?: string;
}

/*
 * Main
 */

export const CallToAction = ({ currentReport, schoolId }: Props): React.ReactElement => {
    const history = useHistory();

    return match(currentReport.status as ReportStatus)
        .with("accountInformation", () => {
            return (
                <CTA
                    color="blue"
                    icon="chart"
                    onClick={(): void => history.push(Routes.account)}
                    subtitle="Continue Your Next Step"
                    title="Add Account Information"
                />
            );
        })
        .with("needsConstituentGroups", () => {
            return (
                <CTA
                    color="blue"
                    icon="chart"
                    onClick={(): void => history.push(Routes.constituentGroups)}
                    subtitle="Continue Your Next Step"
                    title="Submit Constituent Group Sizes"
                />
            );
        })
        .with("collectingData", () => {
            return (
                <CTA
                    color="blue"
                    icon="paper-plane"
                    onClick={(): void => history.push(Routes.promotion)}
                    subtitle="Continue Your Next Step"
                    title="Promote Your Survey"
                />
            );
        })
        .with("readyToGenerate", () => {
            const [isModalOpen, setModalOpen] = useState(false);
            const [generateReport] = useMutation<ApolloMutationData, ApolloMutationVars>(GENERATE_REPORT_MUTATION);

            const generate = async (schoolId: string | undefined): Promise<void> => {
                if (!schoolId) {
                    toast.error("No school ID found, please contact an admin.");
                    return;
                }
                try {
                    const res: any = await generateReport({
                        variables: {
                            input: {
                                functionName: CloudCodeFunction.GenerateReports,
                                params: {
                                    schoolId,
                                },
                            },
                        },
                    });
                    toast.success(res);
                    history.push(Routes.report.home);
                } catch (e: any) {
                    toast.error(parseError(e));
                }
            };

            return (
                <div>
                    <CTA
                        color="gold"
                        icon="file-check"
                        onClick={(): void => {
                            setModalOpen(true);
                        }}
                        subtitle="All Required Surveys Completed"
                        title="Finalize and View Your Reports"
                    />
                    <Modal isOpen={isModalOpen} setOpen={setModalOpen} title="This action cannot be undone">
                        <Body>
                            Congratulations! Your constituent survey responses have reached their minimum thresholds and
                            your report is ready to run. It's important to note that you can only run your report one
                            time. If you have given any constituent groups a deadline that has not yet passed, any
                            future survey completes will not be considered in your results.
                        </Body>
                        <Button
                            onClick={() => {
                                generate(schoolId);
                                setModalOpen(false);
                            }}
                            variation="secondary"
                        >
                            I understand, run the report
                        </Button>
                    </Modal>
                </div>
            );
        })
        .with("reportGenerating", () => <></>)
        .with("reportGenerated", () => {
            return (
                <CTA
                    color="gold"
                    icon="chart"
                    onClick={(): void => history.push(Routes.report.home)}
                    subtitle="All Required Surveys Completed"
                    title="View Your Reports"
                />
            );
        })
        .exhaustive();
};
